/**
 * ThinkingContent
 *
 * Clamps a reasoning step's thinking prose to a fixed window in the inline chat.
 * When the text overflows, it eases down once to reveal there's more, then
 * leaves the viewport scrollable so the reader can scroll back up to re-read.
 *
 * - Fades (top + bottom) signal there's more text above/below the window.
 * - Reduced-motion: no auto-scroll; the box is still scrollable.
 * - Used in compact (inline) mode only; the sidebar history view renders the
 *   full prose unclamped.
 */
import type { ReactNode } from 'react';

import { useThinkingScroll } from './useThinkingScroll';

interface ThinkingContentProps {
  /** Auto-reveal when overflowing (the step is the active/expanded one). */
  animate: boolean;
  /** Changes whenever the prose changes — re-triggers overflow measurement. */
  contentKey: string;
  children: ReactNode;
}

export function ThinkingContent({
  animate,
  contentKey,
  children,
}: ThinkingContentProps) {
  const { containerRef, overflowing, onScroll } = useThinkingScroll(
    animate,
    contentKey
  );

  return (
    <div className='relative'>
      <div
        ref={containerRef}
        onScroll={onScroll}
        className='max-h-[160px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      >
        {children}
      </div>
      {overflowing && (
        <>
          <div className='pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-bg-primary to-transparent' />
          <div className='pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-bg-primary to-transparent' />
        </>
      )}
    </div>
  );
}

export default ThinkingContent;
